import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CashFlowStatementModel,
  DateRange,
  extractDateRange,
  extractNumberArr,
  HxOrganizationService,
  HxReportService,
  HxUiLabelPipe,
  isoDate,
  OrganizationBalanceSnapshotModel,
  OrganizationWithUinModel,
  StoreBasicModel,
  toRouterQueryParams,
  UiLabel
} from 'hx-services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { HxDateRangeSelectComponent, HxDateSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { DecimalPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { OrganizationSelectComponent } from '../../components/organization-select/organization-select.component';

interface SnapshotRow {
  orgId: number;
  title?: UiLabel;
  date: string;
  value: number | null;
  saved: boolean;
  saving: boolean;
}

@Component({
  selector: 'app-cash-flow',
  templateUrl: './cash-flow.component.html',
  imports: [
    FormsModule,
    HxDateRangeSelectComponent,
    HxDateSelectComponent,
    HxLoaderComponent,
    HxStoreSelectComponent,
    OrganizationSelectComponent,
    NgIf,
    NgForOf,
    NgClass,
    TranslocoPipe,
    DecimalPipe,
    HxUiLabelPipe,
  ],
  styleUrls: ['./cash-flow.component.css']
})
export class CashFlowComponent {
  params: {
    organizationIds?: number[],
    storeIds?: number[],
    date?: DateRange,
  } = {};

  isLoading = {
    cashflow: false,
    generate: false,
  };
  cashflow?: CashFlowStatementModel;
  showStoreSelect = true;
  snapshotRows: SnapshotRow[] = [];
  snapshotsExpanded = true;
  private orgOptions: OrganizationWithUinModel[] = [];

  private readonly reportService = inject(HxReportService);
  private readonly orgService = inject(HxOrganizationService);
  private readonly aRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  constructor() {
    this.loadOrganizations();
    this.aRoute.queryParamMap.pipe(takeUntilDestroyed()).subscribe(async paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name)?.split(',').map(mapFn) ?? undefined;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      const orgIds = extractNumberArr('organizationIds', paramMap);
      this.params.organizationIds = orgIds.length ? orgIds : undefined;
      this.params.storeIds = asArrFn('storeIds', str => Number(str));
      console.log('this.params.storeIds = ', this.params.storeIds);
      if (this.params.organizationIds?.length) {
        this.load();
        this.loadSnapshots();
      } else {
        this.cashflow = undefined;
        this.snapshotRows = [];
      }
    });
  }

  resetFilter() {
    this.params = {date: {from: isoDate(new Date()), to: isoDate(new Date())}};
    this.refreshStoreSelect();
    this.navigate();
  }

  onOrganizationChange() {
    this.params.storeIds = undefined;
    this.refreshStoreSelect();
    this.navigate();
  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  onStoreFilterFn: (stores: StoreBasicModel[]) => StoreBasicModel[] = stores => {
    const orgIds = this.params.organizationIds;
    if (!orgIds?.length) return [];
    return stores.filter(s => orgIds.includes(s.organizationId));
  };

  async load() {
    this.cashflow = undefined;
    this.isLoading.cashflow = true;
    try {
      this.cashflow = await this.reportService.getCashFlowReports({
        fromDate: this.params.date!.from,
        toDate: this.params.date!.to,
        storeIds: this.params.storeIds?.length ? this.params.storeIds : undefined,
        organizationIds: this.params.organizationIds
      });
    } finally {
      this.isLoading.cashflow = false;
    }
  }

  async saveSnapshot(row: SnapshotRow) {
    if (row.value === null || row.value === undefined || !row.date) return;
    row.saving = true;
    try {
      await this.reportService.saveOrganizationBalanceSnapshot({
        organizationId: row.orgId,
        date: row.date,
        value: row.value
      });
      row.saved = true;
      await this.load();
    } finally {
      row.saving = false;
    }
  }

  private async loadOrganizations() {
    this.orgOptions = await this.orgService.getMyOrganizations({countryId: undefined as any});
  }

  private async loadSnapshots() {
    const orgIds = this.params.organizationIds ?? [];
    if (!orgIds.length) {
      this.snapshotRows = [];
      return;
    }
    if (!this.orgOptions.length) {
      await this.loadOrganizations();
    }
    const snapshots = await this.reportService.getOrganizationBalanceSnapshots(orgIds);
    const byOrg = new Map<number, OrganizationBalanceSnapshotModel>();
    for (const s of snapshots) byOrg.set(s.organizationId, s);

    this.snapshotRows = orgIds.map(orgId => {
      const existing = byOrg.get(orgId);
      const org = this.orgOptions.find(o => o.organization.id === orgId)?.organization;
      return {
        orgId,
        title: org?.title,
        date: existing?.date ?? isoDate(new Date()),
        value: existing?.value ?? null,
        saved: !!existing,
        saving: false,
      };
    });
  }

  private refreshStoreSelect() {
    this.showStoreSelect = false;
    setTimeout(() => this.showStoreSelect = true);
  }
}
